import { useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, type ButtonSize } from '@src/components/Button/Button';
import { MoonIcon } from '@src/components/icons/MoonIcon';
import { SunIcon } from '@src/components/icons/SunIcon';
import { useTheme } from '@src/theme/useTheme';

const subscribe = () => () => {};

export interface ThemeToggleProps {
  size?: ButtonSize;
  lightLabel?: string;
  darkLabel?: string;
  iconOnly?: boolean;
  testId?: string;
  className?: string;
}

export function ThemeToggle({
  size = 'small',
  lightLabel,
  darkLabel,
  iconOnly = false,
  testId,
  className,
}: ThemeToggleProps) {
  const { t } = useTranslation();
  const hydrated = useSyncExternalStore(subscribe, () => true, () => false);
  const { preference, setPreference } = useTheme();
  // Static pages render Auto. Match that HTML during hydration, then show the
  // saved preference; the blocking head script already paints the correct mode.
  const displayedPreference = useSyncExternalStore(
    subscribe,
    () => preference,
    () => 'auto' as const,
  );
  const options = [
    { value: 'light', label: lightLabel ?? t('theme.light'), icon: <SunIcon /> },
    { value: 'dark', label: darkLabel ?? t('theme.dark'), icon: <MoonIcon /> },
    {
      value: 'auto',
      label: t('theme.auto'),
      icon: (
        <svg
          width="1em"
          height="1em"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M12 3a9 9 0 0 1 0 18Z" fill="currentColor" stroke="none" />
        </svg>
      ),
    },
  ] as const;
  const index = options.findIndex((option) => option.value === displayedPreference);
  const current = options[index];
  const next = options[(index + 1) % options.length];
  const label = t('theme.cycle', { current: current.label, next: next.label });
  return (
    <Button
      variant={iconOnly ? 'ghost' : 'soft'}
      size={size}
      className={className}
      icon={current.icon}
      disabled={!hydrated}
      onClick={() => setPreference(next.value)}
      aria-label={label}
      title={label}
      data-theme-preference={displayedPreference}
      testId={testId}
    >
      {iconOnly ? undefined : current.label}
    </Button>
  );
}
